import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { calculateStudentLoan, STUDENT_LOAN_THRESHOLDS } from '../utils/taxCalculations';
import { updateMetaTags, defaultMeta } from '../utils/metaTags';

const StudentLoanEstimator = () => {
  useEffect(() => {
    updateMetaTags({
      title: 'UK Student Loan Repayment Calculator | Plan 1, 2, 4, 5 & Postgraduate (2025/26)',
      description: 'Calculate your monthly UK student loan repayments based on your salary and loan plan. Includes all UK student loan types (Plan 1, 2, 4, 5, Postgraduate) for 2025/26 tax year.',
      keywords: 'student loan calculator, UK student loan, plan 1, plan 2, plan 4, plan 5, postgraduate loan, student loan repayments, SLC calculator',
      ogTitle: 'UK Student Loan Repayment Calculator (2025/26)',
      ogDescription: 'Calculate your monthly UK student loan repayments based on your salary and loan plan. Includes all UK student loan types.',
      canonical: `${defaultMeta.siteUrl}/student-loan-estimator`,
    });
  }, []);

  const [salary, setSalary] = useState('');
  const [loanPlan, setLoanPlan] = useState('plan2');

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getRepaymentAmount = () => {
    if (!salary || parseFloat(salary) <= 0) return null;
    
    const annualRepayment = calculateStudentLoan(parseFloat(salary), loanPlan);
    return {
      annual: annualRepayment,
      monthly: annualRepayment / 12,
      weekly: annualRepayment / 52
    };
  };

  const repayment = getRepaymentAmount();
  const threshold = STUDENT_LOAN_THRESHOLDS[loanPlan]?.threshold || 0;
  const rate = STUDENT_LOAN_THRESHOLDS[loanPlan]?.rate || 0;

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.section 
        className="text-center space-y-4"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900">
          Student Loan Repayment Estimator (UK)
        </h1>
        <p className="text-lg text-gray-600 max-w-3xl mx-auto">
          Calculate your monthly student loan repayments based on your salary and loan plan. 
          Includes all UK student loan types for 2025/26.
        </p>
      </motion.section>

      {/* Calculator */}
      <motion.section 
        className="bg-white rounded-xl p-6 md:p-8 shadow-sm border max-w-2xl mx-auto"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <h2 className="text-2xl font-semibold text-gray-900 mb-6">Calculate Your Repayments</h2>
        
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Annual Salary (£)
            </label>
            <input
              type="number"
              value={salary}
              onChange={(e) => setSalary(e.target.value)}
              placeholder="e.g. 40000"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Student Loan Plan
            </label>
            <select
              value={loanPlan}
              onChange={(e) => setLoanPlan(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="plan1">Plan 1 (Started before Sept 2012)</option>
              <option value="plan2">Plan 2 (Started Sept 2012 - July 2023)</option>
              <option value="plan4">Plan 4 (Scotland)</option>
              <option value="plan5">Plan 5 (Started Aug 2023 onwards)</option>
              <option value="postgraduate">Postgraduate Loan</option>
            </select>
          </div>

          {/* Plan Details */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-medium text-blue-900 mb-2">Your Plan Details</h3>
            <div className="text-sm text-blue-800 space-y-1">
              <p>• Repayment threshold: {formatCurrency(threshold)}</p>
              <p>• Repayment rate: {(rate * 100).toFixed(0)}% above threshold</p>
              <p>• You only pay if earning above {formatCurrency(threshold)}</p>
            </div>
          </div>

          {/* Results */}
          {repayment && (
            <motion.div 
              className="bg-green-50 border border-green-200 rounded-lg p-6"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              <h3 className="font-semibold text-green-900 mb-4">Your Repayments</h3>
              
              {repayment.annual > 0 ? (
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-sm text-green-600 font-medium">Yearly</p>
                    <p className="text-lg font-bold text-green-800">{formatCurrency(repayment.annual)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-green-600 font-medium">Monthly</p>
                    <p className="text-lg font-bold text-green-800">{formatCurrency(repayment.monthly)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-green-600 font-medium">Weekly</p>
                    <p className="text-lg font-bold text-green-800">{formatCurrency(repayment.weekly)}</p>
                  </div>
                </div>
              ) : (
                <p className="text-center text-green-800 font-medium">
                  No repayments required - salary below threshold
                </p>
              )}
            </motion.div>
          )}
        </div>
      </motion.section>

      {/* Comprehensive Guide */}
      <motion.section 
        className="bg-white rounded-xl p-6 md:p-8 shadow-sm border space-y-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <div>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Complete Guide to UK Student Loan Repayments</h2>
          <p className="text-gray-700 mb-6 leading-relaxed">
            Understanding your student loan repayments is crucial for financial planning. This comprehensive guide explains how UK student loans work, when you'll repay, and strategies for managing your student debt effectively.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="bg-blue-50 rounded-xl p-5 border border-blue-100">
              <h3 className="font-semibold text-gray-900 mb-3 text-lg">Plan 1 (Pre-2012)</h3>
              <ul className="text-sm text-gray-700 space-y-2 leading-relaxed">
                <li>• <strong>Eligibility:</strong> Started university before September 2012</li>
                <li>• <strong>Repayment threshold:</strong> £22,015 per year</li>
                <li>• <strong>Repayment rate:</strong> 9% of income above threshold</li>
                <li>• <strong>Written off:</strong> After 25 years (or when you turn 65, whichever comes first)</li>
                <li>• <strong>Interest rate:</strong> RPI (Retail Price Index) or Bank of England base rate + 1%, whichever is lower</li>
              </ul>
            </div>

            <div className="bg-indigo-50 rounded-xl p-5 border border-indigo-100">
              <h3 className="font-semibold text-gray-900 mb-3 text-lg">Plan 2 (2012-2023)</h3>
              <ul className="text-sm text-gray-700 space-y-2 leading-relaxed">
                <li>• <strong>Eligibility:</strong> Started university September 2012 - July 2023</li>
                <li>• <strong>Repayment threshold:</strong> £27,295 per year</li>
                <li>• <strong>Repayment rate:</strong> 9% of income above threshold</li>
                <li>• <strong>Written off:</strong> After 30 years</li>
                <li>• <strong>Interest rate:</strong> RPI + up to 3% (varies by salary - lower earners pay less interest)</li>
              </ul>
            </div>

            <div className="bg-purple-50 rounded-xl p-5 border border-purple-100">
              <h3 className="font-semibold text-gray-900 mb-3 text-lg">Plan 4 (Scotland)</h3>
              <ul className="text-sm text-gray-700 space-y-2 leading-relaxed">
                <li>• <strong>Eligibility:</strong> Scottish students (regardless of where you study)</li>
                <li>• <strong>Repayment threshold:</strong> £27,660 per year</li>
                <li>• <strong>Repayment rate:</strong> 9% of income above threshold</li>
                <li>• <strong>Written off:</strong> After 30 years</li>
                <li>• <strong>Interest rate:</strong> RPI (typically lower than Plan 2)</li>
              </ul>
            </div>
          </div>

          <div className="space-y-4">
            <div className="bg-emerald-50 rounded-xl p-5 border border-emerald-100">
              <h3 className="font-semibold text-gray-900 mb-3 text-lg">Plan 5 (2023+)</h3>
              <ul className="text-sm text-gray-700 space-y-2 leading-relaxed">
                <li>• <strong>Eligibility:</strong> Started university August 2023 onwards</li>
                <li>• <strong>Repayment threshold:</strong> £25,000 per year (lower than Plan 2)</li>
                <li>• <strong>Repayment rate:</strong> 9% of income above threshold</li>
                <li>• <strong>Written off:</strong> After 40 years (longer than Plan 2)</li>
                <li>• <strong>Interest rate:</strong> RPI (no additional percentage, making it cheaper than Plan 2)</li>
              </ul>
            </div>

            <div className="bg-amber-50 rounded-xl p-5 border border-amber-100">
              <h3 className="font-semibold text-gray-900 mb-3 text-lg">Postgraduate Loan</h3>
              <ul className="text-sm text-gray-700 space-y-2 leading-relaxed">
                <li>• <strong>Eligibility:</strong> Masters or PhD loans (separate from undergraduate loans)</li>
                <li>• <strong>Repayment threshold:</strong> £21,000 per year</li>
                <li>• <strong>Repayment rate:</strong> 6% of income above threshold (lower than undergraduate)</li>
                <li>• <strong>Written off:</strong> After 30 years</li>
                <li>• <strong>Interest rate:</strong> RPI + 3%</li>
                <li>• <strong>Important:</strong> You repay both undergraduate and postgraduate loans simultaneously if you have both</li>
              </ul>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-5">
              <h4 className="font-semibold text-yellow-900 mb-3">Key Facts About Student Loan Repayments</h4>
              <ul className="text-sm text-yellow-800 space-y-2 leading-relaxed">
                <li>• <strong>Automatic deductions:</strong> Repayments are taken automatically through PAYE if you're employed</li>
                <li>• <strong>Self-employed:</strong> You must declare and repay through Self Assessment</li>
                <li>• <strong>Multiple loans:</strong> If you have multiple loan types, you repay them all simultaneously</li>
                <li>• <strong>Below threshold:</strong> Repayments stop automatically if your salary drops below the threshold</li>
                <li>• <strong>Interest rates:</strong> Vary by plan and your salary level (lower earners typically pay less interest)</li>
                <li>• <strong>Overpayments:</strong> You can make voluntary overpayments, but this may not be financially beneficial for most people</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
          <h3 className="font-semibold text-gray-900 mb-4 text-lg">Should You Overpay Your Student Loan?</h3>
          <p className="text-gray-700 mb-4 text-sm leading-relaxed">
            This is one of the most common questions about student loans. The answer depends on your loan plan, salary, and financial situation.
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white rounded-lg p-4 border border-slate-300">
              <h4 className="font-medium mb-2 text-green-700">Consider Overpaying If:</h4>
              <ul className="text-sm text-gray-700 space-y-1">
                <li>• You're a high earner and will likely pay off the loan before it's written off</li>
                <li>• You have Plan 1 (25-year write-off period)</li>
                <li>• You have extra money after building emergency fund and pension contributions</li>
                <li>• The interest rate is higher than your mortgage or other debts</li>
              </ul>
            </div>
            <div className="bg-white rounded-lg p-4 border border-slate-300">
              <h4 className="font-medium mb-2 text-amber-700">Don't Overpay If:</h4>
              <ul className="text-sm text-gray-700 space-y-1">
                <li>• You're on Plan 2 or Plan 5 and unlikely to pay it off before write-off</li>
                <li>• You haven't built an emergency fund (3-6 months expenses)</li>
                <li>• You're not maximizing pension contributions (especially if you're a higher rate taxpayer)</li>
                <li>• You have other high-interest debt (credit cards, personal loans)</li>
                <li>• You're saving for a house deposit (first-time buyer)</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
          <h3 className="font-semibold text-gray-900 mb-4 text-lg">How Student Loan Interest Works</h3>
          <p className="text-gray-700 mb-4 text-sm leading-relaxed">
            Student loan interest is calculated daily and added to your balance. The interest rate varies by plan and, for Plan 2, by your salary level. Lower earners pay less interest, which makes the system progressive.
          </p>
          <div className="bg-white rounded-lg p-4 border border-blue-300 text-sm">
            <h4 className="font-medium mb-2">Interest Rate Examples (2024/25):</h4>
            <ul className="space-y-2 text-gray-700">
              <li>• <strong>Plan 1:</strong> RPI (around 2.5-3%) - fixed rate</li>
              <li>• <strong>Plan 2:</strong> RPI + up to 3% (salary dependent - ranges from ~2.5% to ~7.3%)</li>
              <li>• <strong>Plan 4:</strong> RPI (around 2.5-3%) - fixed rate</li>
              <li>• <strong>Plan 5:</strong> RPI only (around 2.5-3%) - no additional percentage</li>
              <li>• <strong>Postgraduate:</strong> RPI + 3% (around 5.5-6%)</li>
            </ul>
            <p className="mt-3 text-xs text-gray-600 italic">
              Note: Interest rates are updated annually and linked to RPI (Retail Price Index). Plan 2 borrowers earning less than the threshold pay RPI only, while higher earners pay RPI + up to 3%.
            </p>
          </div>
        </div>
      </motion.section>
    </div>
  );
};

export default StudentLoanEstimator;